import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton, Typography, message } from "antd";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import { UiButton, UiTag } from "../../ui";
import { formatPatientAge } from "../../../helpers/formatAge";
import { getPharmacyBillApi } from "../../../services/pharmacyBilling.services";
import PharmacyBillSummaryCards from "./PharmacyBillSummaryCards";
import PharmacyBillMedicines from "./PharmacyBillMedicines";
import PharmacyBillRecordPayment from "./PharmacyBillRecordPayment";
import PharmacyBillPaidCard from "./PharmacyBillPaidCard";
import PharmacyBillPaymentHistory from "./PharmacyBillPaymentHistory";
import RaiseReturnModal from "../Returns/RaiseReturnModal";
import ReturnDetailModal from "../Returns/ReturnDetailModal";
import { getPharmacyReturnApi } from "../../../services/pharmacyReturns.services";
import styles from "../../PatientsIP/ReceptionistIp/BillingIp/BillingIp.module.css";
import headStyles from "./billHead.module.css";
import CgIcon from "../common/CgIcon";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";

const SALE_TYPE_TAG_LABEL = {
  IP: "Admission",
  OP: "OP ID",
  PATIENT: "Patient ID",
};

const BillDetailPage = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnOpen, setReturnOpen] = useState(false);
  const [reviewReturn, setReviewReturn] = useState(null);

  const loadBill = useCallback(() => {
    if (!billId) return;
    setLoading(true);
    getPharmacyBillApi(billId)
      .then((res) => setBill(res?.data))
      .catch(() => {
        setBill(null);
        message.error("Failed to load bill");
      })
      .finally(() => setLoading(false));
  }, [billId]);

  useEffect(() => {
    loadBill();
  }, [loadBill]);

  const customerLabel = bill?.patient_name || bill?.counter_sale_customer_name || "Bill";
  const ageGenderText = formatPatientAge(bill?.patient_age, bill?.patient_gender);
  const referenceLabel =
    bill?.sale_type === "COUNTER"
      ? "Counter Sale"
      : bill?.reference_id
        ? `${SALE_TYPE_TAG_LABEL[bill.sale_type] || bill.sale_type}: ${bill.reference_id}`
        : null;

  const canReturn = bill?.status !== "cancelled" && (bill?.items?.length || 0) > 0;

  const loadReturn = useCallback(
    (id) =>
      getPharmacyReturnApi(id)
        .then((res) => {
          const data = res?.data || null;
          /*
           * A return whose every line has been settled has nothing left to
           * ask about, so the dialog that was asking closes itself. It used
           * to stay open behind the reason dialog, still offering Approve and
           * Reject on items that had just been decided.
           */
          const undecided = (data?.items || []).some((item) =>
            ["pending", "requested"].includes(item?.status)
          );
          setReviewReturn(undecided ? data : null);
        })
        .catch(() => {}),
    []
  );

  /*
   * A return is raised and then settled, and the two are one errand.
   *
   * Submitting the return only asks for it - every line on it opens pending,
   * and nothing goes back to stock until somebody approves it. So the return
   * that was just raised opens straight away for that decision, rather than
   * leaving the pharmacist to find it again on the Returns screen.
   *
   * This used to happen for an OP or a counter sale alone. Approving a return
   * is the pharmacy's decision about its own stock whatever the sale was, and
   * an IP return raised here was the one that got away - closed on a success
   * message, still pending, with nothing on screen to say so.
   */
  const onReturnCreated = (created) => {
    if (!created?.id) {
      setReviewReturn(null);
      return;
    }
    loadReturn(created.id);
  };

  return (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        <></>
      }
    >
      <div className={styles.page}>
        {loading ? (
          <Skeleton active />
        ) : !bill ? (
          <Typography.Text type="secondary">Bill not found.</Typography.Text>
        ) : (
          <>
            {/*
              * Who, which bill, and the way out. The facts that identify the
              * bill sit on the second line at the weight of a caption, so the
              * first line is only ever the two things you came here for.
              */}
            <div className={headStyles.head}>
              <button
                type="button"
                className={headStyles.back}
                onClick={() => navigate("/pharmacy/billing")}
                aria-label="Back to billing"
                title="Back to Billing"
              >
                <CgIcon name="back" size={18} stroke={2.1} />
              </button>

              <div className={headStyles.who}>
                <div className={headStyles.name}>
                  {customerLabel}
                  {bill.sale_type && (
                    <UiTag tone="info" size="sm">
                      {SALE_TYPE_TAG_LABEL[bill.sale_type] || bill.sale_type}
                    </UiTag>
                  )}
                  <span className={headStyles.ref}>{bill.bill_number}</span>
                </div>

                <div className={headStyles.meta}>
                  {bill.patient_mrn && (
                    <span className={headStyles.mono}>{bill.patient_mrn}</span>
                  )}
                  {ageGenderText && (
                    <>
                      {bill.patient_mrn && <span className={headStyles.sep} />}
                      <span>{ageGenderText}</span>
                    </>
                  )}
                  {referenceLabel && (
                    <>
                      <span className={headStyles.sep} />
                      <span>{referenceLabel}</span>
                    </>
                  )}
                  {bill.patient_mobile && (
                    <>
                      <span className={headStyles.sep} />
                      <span>{bill.patient_mobile}</span>
                    </>
                  )}
                  {bill.order_number && (
                    <>
                      <span className={headStyles.sep} />
                      <span>Order {bill.order_number}</span>
                    </>
                  )}
                </div>
              </div>

              <div className={headStyles.acts}>
                <UiButton variant="secondary" onClick={() => navigate("/pharmacy/billing")}>
                  Close
                </UiButton>
              </div>
            </div>

            {/*
              * The four figures run the full width, above the pair of columns
              * rather than inside the left one.
              *
              * Nested in the column they had the medicines card's width to
              * share between four of them, so each tile was about 150px and the
              * amounts broke across two lines - the one row of the page that is
              * only numbers was the hardest row to read.
              */}
            <PharmacyBillSummaryCards bill={bill} />

            <div className={styles.columns}>
              <div className={styles.leftColumn}>
                <PharmacyBillMedicines bill={bill} />
                {canReturn && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <UiButton variant="secondary" onClick={() => setReturnOpen(true)}>
                      Raise Return
                    </UiButton>
                  </div>
                )}
              </div>
              <aside className={styles.rightColumn}>
                {/* Nothing left to collect, so nothing offering to collect it -
                    the column keeps what was paid and how, and drops the form. */}
                {Number(bill.due_amount) > 0.005 && bill.status !== "cancelled" ? (
                  <PharmacyBillRecordPayment bill={bill} refreshData={loadBill} />
                ) : null}
                <PharmacyBillPaidCard bill={bill} />
                <PharmacyBillPaymentHistory bill={bill} />
              </aside>
            </div>
          </>
        )}
      </div>

      {returnOpen && bill && (
        <RaiseReturnModal
          open={returnOpen}
          setOpen={setReturnOpen}
          bill={bill}
          refreshPage={loadBill}
          onCreated={onReturnCreated}
        />
      )}

      {reviewReturn && (
        <ReturnDetailModal
          open
          setOpen={(next) => {
            if (!next) setReviewReturn(null);
          }}
          returnRequest={reviewReturn}
          refreshPage={() => {
            loadBill();
            loadReturn(reviewReturn.id);
          }}
        />
      )}
    </CustomLayout>
  );
};

export default BillDetailPage;
